/* Peças compartilhadas entre Procurações e Certificados -- as duas telas
   são o mesmo controle de vencimento (uma linha por cliente, quadrinhos
   coloridos, cards de resumo no topo, alerta ao abrir), só muda o que é
   cadastrado em cada quadrinho. */
import React from 'react';
import { FiAlertTriangle, FiAlertCircle, FiClock, FiUsers } from 'react-icons/fi';
import { faixaProcuracao, FAIXAS_ALERTA } from '../../../services/procuracoes';
import styles from './Procuracoes.module.css';
import useEsc from '../../../hooks/useEsc';

export function formatarData(iso) {
  if (!iso) return null;
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
}

/* `masculino` pro certificado ("vencido"), procuração é "vencida" */
export function textoPrazo(dias, masculino = false) {
  if (dias === null || dias === undefined) return 'sem vencimento';
  if (dias < 0)  return `${masculino ? 'vencido' : 'vencida'} há ${Math.abs(dias)} ${Math.abs(dias) === 1 ? 'dia' : 'dias'}`;
  if (dias === 0) return 'vence hoje';
  return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

/* ─── Cards de resumo ───
   Contam CLIENTES, não documentos: o clique filtra a lista pelos clientes
   naquela condição, então o número tem que bater com as linhas que
   aparecem depois do clique. 15 e 30 dias se sobrepõem de propósito
   ("próximos 30 dias" inclui os próximos 15), igual ao outro sistema. */
const FILTROS = {
  vencidos: d => d < 0,
  dias15:   d => d >= 0 && d <= 15,
  dias30:   d => d >= 0 && d <= 30,
};

/* ids dos clientes que têm pelo menos um item na condição do filtro;
   null = sem filtro (todos) */
export function clientesDoFiltro(itens, filtro) {
  const teste = FILTROS[filtro];
  if (!teste) return null;
  return new Set(itens
    .filter(i => i.diasRestantes !== null && i.diasRestantes !== undefined && teste(i.diasRestantes))
    .map(i => i.clienteId));
}

/* extras: cards que abrem outra coisa em vez de filtrar (ex.: "Falta certificado") */
export function CardsResumo({ itens, totalClientes, filtro, onFiltro, extras = [] }) {
  const cards = [
    { id: null,       icone: FiUsers,         cor: styles.kpiAzul,     valor: totalClientes,
      titulo: 'Clientes',   sub: 'Na lista' },
    { id: 'vencidos', icone: FiAlertTriangle, cor: styles.kpiVermelho,
      titulo: 'Com vencidos', sub: 'Ação necessária' },
    { id: 'dias15',   icone: FiAlertCircle,   cor: styles.kpiLaranja,
      titulo: 'Vencimentos', sub: 'Próximos 15 dias' },
    { id: 'dias30',   icone: FiClock,         cor: styles.kpiRoxo,
      titulo: 'Vencimentos', sub: 'Próximos 30 dias' },
  ];
  return (
    <div className={styles.kpis} style={extras.length ? { gridTemplateColumns: `repeat(${cards.length + extras.length}, minmax(0, 1fr))` } : undefined}>
      {cards.map(c => {
        const Icone = c.icone;
        const valor = c.id ? clientesDoFiltro(itens, c.id).size : c.valor;
        const ativo = filtro === c.id;
        return (
          <button key={c.id || 'todos'} type="button"
            className={`${styles.kpi} ${ativo ? styles.kpiAtivo : ''}`}
            /* clicar de novo no card ativo volta pra lista inteira */
            onClick={() => onFiltro(ativo ? null : c.id)}>
            <span className={`${styles.kpiIcone} ${c.cor}`}><Icone size={17} /></span>
            <span className={styles.kpiTexto}>
              <strong className={styles.kpiValor}>{valor}</strong>
              <span className={styles.kpiTitulo}>{c.titulo}</span>
              <span className={styles.kpiSub}>{c.sub}</span>
            </span>
          </button>
        );
      })}
      {extras.map(c => {
        const Icone = c.icone;
        return (
          <button key={c.chave} type="button" className={styles.kpi} onClick={c.onClick}
            style={c.vermelho && c.valor > 0 ? { borderColor: '#dc2626', background: '#fef2f2' } : undefined}>
            <span className={`${styles.kpiIcone} ${styles.kpiVermelho}`}><Icone size={17} /></span>
            <span className={styles.kpiTexto}>
              <strong className={styles.kpiValor} style={c.vermelho && c.valor > 0 ? { color: '#b91c1c' } : undefined}>{c.valor}</strong>
              <span className={styles.kpiTitulo}>{c.titulo}</span>
              <span className={styles.kpiSub}>{c.sub}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ─── Alerta de vencimento ───
   Aparece ao abrir a tela quando há item vencendo dentro de 15 dias.
   O "não mostrar de novo" é por item E por faixa (15/10/5): dispensar o
   aviso de 15 dias não cala o de 10 nem o de 5, senão o alerta que existe
   justamente pra apertar quando o prazo encurta nunca mais voltaria.
   Também some sozinho quando o item é renovado, porque a chave inclui o
   vencimento. Cada tela tem a sua chave no localStorage. */
function faixaAlerta(dias) {
  if (dias === null || dias === undefined || dias < 0) return null;
  return FAIXAS_ALERTA.find(f => dias <= f) ?? null;
}

function lerDispensados(chave) {
  try { return JSON.parse(localStorage.getItem(chave)) || {}; } catch { return {}; }
}
function dispensar(chave, ids) {
  try {
    const atual = lerDispensados(chave);
    ids.forEach(k => { atual[k] = true; });
    localStorage.setItem(chave, JSON.stringify(atual));
  } catch { /* sem localStorage: o alerta volta na próxima visita, tudo bem */ }
}

/* o que entra no alerta: vencendo dentro de 15 dias (ou já vencido) e que
   o usuário ainda não dispensou naquela faixa */
export function itensDoAlerta(itens, chave) {
  const dispensados = lerDispensados(chave);
  return itens
    .filter(p => p.diasRestantes !== null && p.diasRestantes !== undefined && p.diasRestantes <= 15)
    .map(p => {
      const faixa = p.diasRestantes < 0 ? 'vencida' : faixaAlerta(p.diasRestantes);
      return { ...p, chaveAlerta: `${p.id}:${p.vencimento}:${faixa}` };
    })
    .filter(p => !dispensados[p.chaveAlerta])
    .sort((a, b) => a.diasRestantes - b.diasRestantes);
}

/* `descricao` é a linha de baixo de cada item (tipo + CPF/CNPJ na
   procuração, nome no certificado) */
export function ModalAlerta({ itens, titulo, singular, plural, chave, masculino, onFechar }) {
  useEsc(onFechar);
  return (
    <div className={styles.modalFundo}>
      <div className={styles.modalCaixa} onClick={e => e.stopPropagation()}>
        <div className={styles.alertaTopo}>
          <div className={styles.alertaIcone}><FiAlertTriangle size={18} /></div>
          <div>
            <h3 className={styles.modalTitulo}>{titulo}</h3>
            <p className={styles.modalSub}>
              {itens.length === 1 ? `1 ${singular} precisa` : `${itens.length} ${plural} precisam`} de atenção.
            </p>
          </div>
        </div>

        <div className={styles.alertaLista}>
          {itens.map(i => (
            <div key={i.id} className={`${styles.alertaLinha} ${styles[faixaProcuracao(i.diasRestantes)]}`}>
              <div>
                <div className={styles.alertaCliente}>{i.codigo} · {i.clienteNome}</div>
                <div className={styles.alertaTipo}>{i.descricao}</div>
              </div>
              <div className={styles.alertaPrazo}>
                <strong>{textoPrazo(i.diasRestantes, masculino)}</strong>
                <span>{formatarData(i.vencimento)}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.modalRodape}>
          <button type="button" className={styles.btnSecundario}
            onClick={() => { dispensar(chave, itens.map(i => i.chaveAlerta)); onFechar(); }}>
            Não mostrar de novo
          </button>
          <button type="button" className={styles.btnPrimario} onClick={onFechar}>Entendi</button>
        </div>
      </div>
    </div>
  );
}
